import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { get as getUser } from 'js/lib/user';

import GetSkillAreaCourseItemsQuery, {
  type GetSkillAreaCourseItemsQueryData,
  type GetSkillAreaCourseItemsQueryVariables,
} from 'bundles/assess-common/api/GetSkillAreaCourseItems';
import toHumanReadableTypeName from 'bundles/learner-progress/utils/toHumanReadableTypeName';
import GetCourseItemInfoQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetCourseItemInfo.graphql';
import type {
  GetCourseItemInfoQuery as GetCourseItemInfoData,
  GetCourseItemInfoQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetCourseItemInfo';

const VERIFIED_SKILLS_ASSESSMENT_FEATURE = 'VERIFIED_SKILLS_ASSESSMENT';

export type SkillAssessmentItem = {
  itemId: string;
  name: string;
  slug: string;
  totalWorkDuration: number;
  isPassedOrCompleted: boolean;
  contentSummary: {
    __typename: string;
  };
};

type Props = {
  courseId: string;
  courseSlug: string;
  skip?: boolean;
};

/**
 * Hook that eagerly fetches all skill assessment items in a course on mount.
 * Returns the target assessment (first incomplete, or last if all complete)
 * along with item details for display (name, totalWorkDuration, etc.)
 */
const useGetSkillAssessmentItems = ({ courseId, courseSlug, skip }: Props) => {
  const userId = getUser().id;
  // Step 1: Fetch all item IDs in the course
  const { data: itemsInCourseData, loading: itemsLoading } = useQuery<
    GetSkillAreaCourseItemsQueryData,
    GetSkillAreaCourseItemsQueryVariables
  >(GetSkillAreaCourseItemsQuery, {
    variables: {
      courseId: courseId || '',
      learnerId: `${userId}` || '',
    },
    fetchPolicy: 'cache-first',
  });

  const skillAssessmentItemIdsInCourse = useMemo<string[]>(
    () =>
      itemsInCourseData?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2
        ?.filter((item) => {
          if (!item?.id) return false;
          const contentSummary = item.contentSummary;
          if (contentSummary?.typeName === 'staffGraded') {
            return contentSummary?.definition?.assignmentFeatures?.includes(VERIFIED_SKILLS_ASSESSMENT_FEATURE);
          }
          return false;
        })
        .map((item) => item.id) || [],
    [itemsInCourseData]
  );

  // Step 2: Fetch details for all items (with completion status and assignment features)
  const { data: itemInfoData, loading: itemInfoLoading } = useQuery<
    GetCourseItemInfoData,
    GetCourseItemInfoQueryVariables
  >(GetCourseItemInfoQuery, {
    variables: {
      courseId,
      itemIds: skillAssessmentItemIdsInCourse,
      limit: skillAssessmentItemIdsInCourse.length,
    },
    skip: skip || !courseId || skillAssessmentItemIdsInCourse.length === 0,
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  // Step 3: Process items - filter for skill assessments and determine target
  const result = useMemo(() => {
    if (!itemInfoData || skillAssessmentItemIdsInCourse.length === 0) {
      return {
        skillAssessmentItems: [] as SkillAssessmentItem[],
        targetAssessment: undefined as SkillAssessmentItem | undefined,
        targetAssessmentLink: undefined as string | undefined,
        courseStartLink: `/learn/${courseSlug}/home/welcome`,
      };
    }

    // Create a map of itemId to index to preserve the order from itemIdsInCourse
    const itemIdToIndex = new Map<string, number>();
    skillAssessmentItemIdsInCourse.forEach((itemId, index) => {
      itemIdToIndex.set(itemId, index);
    });

    const elements = itemInfoData?.NaptimeQueries?.OnDemandLearnerMaterialItemsV1Resource?.items?.elements || [];

    // Sort items by their original course order
    const orderedItems = [...elements].sort((itemA, itemB) => {
      const indexA = itemIdToIndex.get(itemA?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
      const indexB = itemIdToIndex.get(itemB?.itemId || '') ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });

    // Filter for verified skill assessments
    const skillAssessmentItems: SkillAssessmentItem[] = orderedItems.map((item) => ({
      itemId: item!.itemId,
      name: item?.name || '',
      slug: item?.slug || '',
      totalWorkDuration: item?.totalWorkDuration || 0,
      isPassedOrCompleted: item?.isPassedOrCompleted ?? false,
      contentSummary: {
        __typename: item?.contentSummary?.__typename || '',
      },
    }));

    // Find first incomplete assessment, or fall back to last assessment if all are completed
    const firstIncompleteAssessment = skillAssessmentItems.find((item) => !item.isPassedOrCompleted);
    const targetAssessment =
      firstIncompleteAssessment ?? skillAssessmentItems[skillAssessmentItems.length - 1] ?? undefined;

    const targetAssessmentLink = targetAssessment
      ? `/learn/${courseSlug}/assignment-submission/${targetAssessment.itemId}`
      : undefined;

    // Also compute course start link (first uncompleted item in course)
    const firstUncompletedItem = orderedItems.find((item) => !item?.isPassedOrCompleted);
    const firstItem = firstUncompletedItem || orderedItems[0];
    let courseStartLink = `/learn/${courseSlug}/home/welcome`;

    if (firstItem?.contentSummary?.__typename && firstItem?.itemId) {
      const itemTypeSlug = firstItem.contentSummary.__typename
        .replace('OnDemandLearnerMaterialItemsV1_', '')
        .replace('Member', '');
      courseStartLink = `/learn/${courseSlug}/${itemTypeSlug}/${firstItem.itemId}`;
    }

    return {
      skillAssessmentItems,
      targetAssessment,
      targetAssessmentLink,
      courseStartLink,
    };
  }, [itemInfoData, skillAssessmentItemIdsInCourse, courseSlug]);

  return {
    ...result,
    loading: itemsLoading || itemInfoLoading,
  };
};

export const getItemTypeLabel = (typename: string): string => {
  return toHumanReadableTypeName(typename.replace('OnDemandLearnerMaterialItemsV1_', '').replace('Member', '')) || '';
};

export default useGetSkillAssessmentItems;
